// Dashboard page component
import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { Card, Typography, Alert, Button, Space } from 'antd';
import { DownloadOutlined } from '@ant-design/icons';
import { useMobile } from '../../hooks/useMobile';
import { useDashboardData } from '../../hooks/useDashboardData';
import { useBaseline } from '../../hooks/useBaseline';
import { DashboardHeader } from '../../components/dashboard/DashboardHeader';
import { DashboardFilters } from '../../components/dashboard/DashboardFilters';
import { DataStatusAlert } from '../../components/dashboard/DataStatusAlert';
import { TierCard } from '../../components/dashboard/TierCard';
import { SkeletonLoader } from '../../components/dashboard/SkeletonLoader';
import { getDashboardColumns } from '../../components/dashboard/DashboardColumns';
import { GlobalSortBar } from '../../components/dashboard/GlobalSortBar';
import { DashboardSummary } from '../../components/dashboard/DashboardSummary';
import { FlagGuide } from '../../components/dashboard/FlagGuide';
import { formatDisplayDate } from '../../utils/formatters';
import { detectAnomalies, AnomalyFlag } from '../../utils/anomalyDetection';
import {
  getBottomPerformers,
  getPriorWeekDateRange,
  exportBottomPerformersToCSV as exportBottomPerformersToPDF,
  BottomPerformer,
} from '../../utils/bottomPerformers';
import DashboardService, { LocationData, DashboardData, SalesTier } from '../../services/DashboardService';
import { BaselineData } from '../../services/baselineService';
import { calculateBaselineAverages, filterLocationsByEightWeekRanges } from '../../services/baselineService';
import { GlobalSortState } from '../../constants/sortableColumns';

const { Text } = Typography;

const Dashboard: React.FC = () => {
  const isMobile = useMobile();
  const [sortBy, setSortBy] = useState<string>('sales');
  const [viewMode, setViewMode] = useState<'weekly' | 'quarterly'>('weekly');
  const [isWeekSelectorOpen, setIsWeekSelectorOpen] = useState(false);
  const [anomalyFlags, setAnomalyFlags] = useState<Map<string, AnomalyFlag>>(new Map());
  const [bottomPerformers, setBottomPerformers] = useState<Map<string, BottomPerformer>>(new Map());
  const [salesTierData, setSalesTierData] = useState<DashboardData | null>(null);
  const [baselineDataByTier, setBaselineDataByTier] = useState<Record<string, BaselineData>>({});
  const [globalSort, setGlobalSort] = useState<GlobalSortState>({ columnKey: null, order: null });

  const {
    loading,
    error,
    dashboardData,
    dateRanges,
    selectedDateRange,
    setSelectedDateRange,
    dataStatus,
    setDataStatus,
    availableQuarters,
    selectedQuarter,
    setSelectedQuarter,
    setError,
  } = useDashboardData({ viewMode, sortBy });

  const { getBaselinePeriod, getTierBaselineData, baselineLoading, baselineChangeLoading } = useBaseline({
    dateRanges,
    selectedDateRange,
    viewMode,
    sortBy,
  });

  // Create a filter function that uses the 8-week range logic (same as baseline)
  // This ensures both baseline and regular average use the same filtering logic
  const filterByEightWeekRanges = useCallback((location: LocationData) => {
    if (!selectedDateRange || !dateRanges || dateRanges.length === 0) {
      return true; // Include all if we can't filter
    }
    const filtered = filterLocationsByEightWeekRanges([location], selectedDateRange.endDate, dateRanges);
    return filtered.length > 0;
  }, [selectedDateRange, dateRanges]);

  // Pull recently-opened stores (fewer than 8 weeks of history) out of the sales
  // tiers into their own "New Stores" section, so they don't skew the tiers and
  // are clearly flagged as still ramping up. Weekly view only — the 8-week filter
  // doesn't apply to quarterly.
  const renderTiers = useMemo<SalesTier[]>(() => {
    const tiers = dashboardData?.salesTiers ?? [];
    if (viewMode !== 'weekly' || !selectedDateRange || !dateRanges || dateRanges.length === 0) {
      return tiers;
    }
    const newStores: LocationData[] = [];
    const trimmed = tiers.map((t) => {
      const established = (t.locations ?? []).filter((loc) => {
        const isEstablished = filterByEightWeekRanges(loc);
        if (!isEstablished) newStores.push(loc);
        return isEstablished;
      });
      return { ...t, locations: established, locationCount: established.length };
    });
    if (newStores.length === 0) return trimmed;
    const newStoresTier: SalesTier = {
      tierName: 'New Stores',
      tierRange: 'Fewer than 8 weeks of history',
      avgGfsPercent: 0,
      locationCount: newStores.length,
      locations: newStores,
    };
    return [...trimmed, newStoresTier];
  }, [dashboardData, viewMode, selectedDateRange, dateRanges, filterByEightWeekRanges]);

  const handleViewModeChange = useCallback(
    (value: 'weekly' | 'quarterly') => {
      setViewMode(value);
      // Reset global sort when view mode changes since columns differ
      setGlobalSort({ columnKey: null, order: null });
      if (value === 'weekly') {
        setSelectedQuarter(null);
      } else {
        setSelectedDateRange(null);
      }
    },
    [setSelectedQuarter, setSelectedDateRange]
  );

  const handleClearGlobalSort = useCallback(() => {
    setGlobalSort({ columnKey: null, order: null });
  }, []);

  const handleQuarterSelect = useCallback(
    (quarter: string, year: number) => {
      // Validate that year is a valid number before setting
      if (isNaN(year) || year <= 0) {
        console.error('Invalid year provided to handleQuarterSelect:', year);
        return;
      }
      setSelectedQuarter({ quarter, year });
    },
    [setSelectedQuarter]
  );

  const getButtonText = useCallback(() => {
    if (!selectedDateRange) return 'Select reporting week';
    
    const dateRangeText = `${formatDisplayDate(selectedDateRange.startDate)} → ${formatDisplayDate(selectedDateRange.endDate)}`;
    const baselineInfo = getBaselinePeriod(selectedDateRange.startDate, selectedDateRange.endDate);
    
    if (baselineInfo) {
      const baselineText = `Baseline: ${formatDisplayDate(baselineInfo.baselineStart)} → ${formatDisplayDate(baselineInfo.baselineEnd)}`;
      return `${dateRangeText} | ${baselineText}`;
    }
    
    return dateRangeText;
  }, [selectedDateRange, getBaselinePeriod]);

  // Calculate anomalies when dashboard data changes
  useEffect(() => {
    if (!dashboardData || viewMode === 'quarterly' || !dashboardData.salesTiers) {
      setAnomalyFlags(new Map());
      return;
    }

    const allLocations: LocationData[] = [];
    dashboardData.salesTiers.forEach(tier => {
      allLocations.push(...tier.locations);
    });

    // Thresholds live in DEFAULT_ANOMALY_CONFIG so FlagGuide shows the same numbers
    const flags = detectAnomalies(allLocations);

    console.warn('Anomaly detection results:', {
      totalLocations: allLocations.length,
      flagsFound: flags.length,
      flags: flags.map(f => ({ location: f.location.location, reason: f.reason })),
      sampleLocations: allLocations.slice(0, 3).map(l => ({
        location: l.location,
        weeklyTotal: l.weeklyTotal,
        gfsPercent: l.gfsPercent,
      })),
    });

    const flagsMap = new Map<string, AnomalyFlag>();
    flags.forEach(flag => {
      flagsMap.set(flag.location.location, flag);
    });
    setAnomalyFlags(flagsMap);
  }, [dashboardData, viewMode]);

  // Fetch sales tier data and baseline when in DM view
  useEffect(() => {
    if (sortBy !== 'dm' || !selectedDateRange || viewMode !== 'weekly' || dateRanges.length === 0) {
      setSalesTierData(null);
      setBaselineDataByTier({});
      return;
    }

    const fetchSalesTierData = async () => {
      try {
        // Fetch dashboard data with sortBy='sales' to get sales tier groupings
        const data = await DashboardService.getDashboardData(
          selectedDateRange.startDate,
          selectedDateRange.endDate,
          'sales'
        );
        setSalesTierData(data);

        // Get baseline week info
        const baselineInfo = getBaselinePeriod(selectedDateRange.startDate, selectedDateRange.endDate);
        if (!baselineInfo) {
          // If no baseline period, use current data as baseline
          const baselineMap: Record<string, BaselineData> = {};
          data.salesTiers.forEach(tier => {
            // Pass the end date of the selected date range and date ranges to filter out locations that haven't existed for 8 ranges
            baselineMap[tier.tierName] = calculateBaselineAverages(tier.locations, undefined, selectedDateRange.endDate, dateRanges);
          });
          setBaselineDataByTier(baselineMap);
          return;
        }

        // Fetch baseline week data for sales tiers
        try {
          const baselineData = await DashboardService.getDashboardData(
            baselineInfo.baselineStart,
            baselineInfo.baselineEnd,
            'sales'
          );

          // Calculate baseline for each sales tier from baseline week data
          const baselineMap: Record<string, BaselineData> = {};
          baselineData.salesTiers.forEach(tier => {
            // Pass the end date of the baseline week and date ranges to filter out locations that haven't existed for 8 ranges
            baselineMap[tier.tierName] = calculateBaselineAverages(tier.locations, undefined, baselineInfo.baselineEnd, dateRanges);
          });
          setBaselineDataByTier(baselineMap);
        } catch (error) {
          console.warn('Failed to fetch baseline week data, using current data:', error);
          // Fallback to current data as baseline
          const baselineMap: Record<string, BaselineData> = {};
          data.salesTiers.forEach(tier => {
            // Pass the end date of the selected date range and date ranges to filter out locations that haven't existed for 8 ranges
            baselineMap[tier.tierName] = calculateBaselineAverages(tier.locations, undefined, selectedDateRange.endDate, dateRanges);
          });
          setBaselineDataByTier(baselineMap);
        }
      } catch (error) {
        console.error('Failed to fetch sales tier data for DM export:', error);
        setSalesTierData(null);
        setBaselineDataByTier({});
      }
    };

    fetchSalesTierData();
  }, [sortBy, selectedDateRange, viewMode, dateRanges, getBaselinePeriod]);

  // Fetch prior week data and calculate bottom performers for weekly view
  useEffect(() => {
    if (!dashboardData || !selectedDateRange || viewMode !== 'weekly') {
      setBottomPerformers(new Map());
      return;
    }

    const fetchPriorWeekData = async () => {
      try {
        const priorWeekRange = getPriorWeekDateRange(selectedDateRange);
        const priorWeekData = await DashboardService.getDashboardData(
          priorWeekRange.startDate,
          priorWeekRange.endDate,
          sortBy
        );

        // Extract sales by location from prior week
        const salesMap = new Map<string, number>();
        priorWeekData.salesTiers.forEach(tier => {
          tier.locations.forEach(loc => {
            salesMap.set(loc.location, loc.weeklyTotal);
          });
        });

        // Calculate bottom performers
        const allLocations: LocationData[] = [];
        dashboardData.salesTiers.forEach(tier => {
          allLocations.push(...tier.locations);
        });

        const performers = getBottomPerformers(allLocations, salesMap, true);
        console.warn('Bottom performers calculation:', {
          totalLocations: allLocations.length,
          priorWeekSalesCount: salesMap.size,
          performersFound: performers.length,
          performers: performers.map(p => ({
            location: p.location.location,
            rank: p.rank,
            gfsPercent: p.gfsPercent,
            priorWeekSales: p.priorWeekSales,
            ratio: p.ratio,
          })),
          sampleLocations: allLocations.slice(0, 3).map(l => ({
            location: l.location,
            eightWeekGfsPercent: l.eightWeekGfsPercentValue ?? (l.eightWeekTotalSales > 0 && l.eightWeekTotalGfs ? (l.eightWeekTotalGfs / l.eightWeekTotalSales) * 100 : null),
            eightWeekTotalGfs: l.eightWeekTotalGfs,
            eightWeekTotalSales: l.eightWeekTotalSales,
          })),
        });
        
        // Also try without prior week data to see if we get more results
        const performersWithoutPriorWeek = getBottomPerformers(allLocations, new Map(), true);
        console.warn('Bottom performers WITHOUT prior week data:', {
          performersFound: performersWithoutPriorWeek.length,
          performers: performersWithoutPriorWeek.map(p => ({
            location: p.location.location,
            rank: p.rank,
            gfsPercent: p.gfsPercent,
          })),
        });
        
        const performersMap = new Map<string, BottomPerformer>();
        performers.forEach(p => {
          performersMap.set(p.location.location, p);
        });
        setBottomPerformers(performersMap);
      } catch (error) {
        console.error('Failed to fetch prior week data:', error);
        setBottomPerformers(new Map());
      }
    };

    fetchPriorWeekData();
  }, [dashboardData, selectedDateRange, viewMode, sortBy]);

  const handleExportBottomPerformers = useCallback(() => {
    if (viewMode !== 'weekly' || !dashboardData) {
      return;
    }
    const performers = Array.from(bottomPerformers.values()).sort((a, b) => a.rank - b.rank);
    if (performers.length === 0) {
      return;
    }
    const filename = selectedDateRange
      ? `bottom_performers_${selectedDateRange.startDate}_${selectedDateRange.endDate}.pdf`
      : 'bottom_performers.pdf';
    exportBottomPerformersToPDF(performers, dashboardData.salesTiers, filename);
  }, [bottomPerformers, selectedDateRange, viewMode, dashboardData]);

  // Recreate columns when flags/performers change
  const columns = React.useMemo(() => {
    return getDashboardColumns({
      sortBy,
      viewMode,
      dataStatus,
      anomalyFlags,
      bottomPerformers,
      selectedDateRange,
    });
  }, [sortBy, viewMode, dataStatus, anomalyFlags, bottomPerformers, selectedDateRange]);

  // Debug: Log when columns are created
  useEffect(() => {
    if (dashboardData) {
      console.warn('Dashboard columns created with:', {
        anomalyFlagsSize: anomalyFlags.size,
        bottomPerformersSize: bottomPerformers.size,
        viewMode,
        hasAnomalyFlags: anomalyFlags.size > 0,
        hasBottomPerformers: bottomPerformers.size > 0,
        anomalyFlagKeys: Array.from(anomalyFlags.keys()).slice(0, 5),
        bottomPerformerKeys: Array.from(bottomPerformers.keys()).slice(0, 5),
      });
    }
  }, [columns, anomalyFlags, bottomPerformers, dashboardData, viewMode]);
  
  return (
    <div style={{ position: 'relative' }}>
      <div style={{
        marginBottom: 16,
        display: 'flex',
        flexDirection: isMobile ? 'column' : 'row',
        justifyContent: 'space-between',
        alignItems: isMobile ? 'stretch' : 'flex-start',
        gap: isMobile ? 12 : 16,
        flexWrap: 'wrap',
      }}>
        <DashboardHeader sortBy={sortBy} />
        <DashboardFilters
          viewMode={viewMode}
          sortBy={sortBy}
          loading={loading}
          isMobile={isMobile}
          dateRanges={dateRanges}
          selectedDateRange={selectedDateRange}
          availableQuarters={availableQuarters}
          selectedQuarter={selectedQuarter}
          isWeekSelectorOpen={isWeekSelectorOpen}
          onViewModeChange={handleViewModeChange}
          onSortByChange={setSortBy}
          onDateRangeSelect={setSelectedDateRange}
          onQuarterSelect={handleQuarterSelect}
          onWeekSelectorOpen={() => setIsWeekSelectorOpen(true)}
          onWeekSelectorClose={() => setIsWeekSelectorOpen(false)}
          getBaselinePeriod={getBaselinePeriod}
          getButtonText={getButtonText}
        />
      </div>

      {error && (
        <Alert
          message="Error"
          description={error}
          type="error"
          closable
          onClose={() => setError(null)}
          style={{ marginBottom: 16 }}
        />
      )}

      <DataStatusAlert
        dataStatus={dataStatus}
        viewMode={viewMode}
          onClose={() => setDataStatus(null)}
        />

      {loading && !dashboardData ? (
        <SkeletonLoader tierCount={undefined} isMobile={isMobile} columns={columns} />
      ) : loading && dashboardData ? (
        <SkeletonLoader tierCount={dashboardData.salesTiers?.length || 0} isMobile={isMobile} columns={columns} />
      ) : dashboardData && dashboardData.salesTiers ? (
        <div>
          <Card style={{ marginBottom: 16 }}>
            <Space
              direction={isMobile ? 'vertical' : 'horizontal'}
              style={{ width: '100%', justifyContent: isMobile ? 'flex-start' : 'space-between' }}
            >
              <Text strong>Period: {dashboardData.dateRangeLabel}</Text>
              {viewMode === 'weekly' && bottomPerformers.size > 0 && (
                <Button
                  type="primary"
                  icon={<DownloadOutlined />}
                  onClick={handleExportBottomPerformers}
                  size="small"
                  block={isMobile}
                >
                  Export Bottom Performers PDF
                </Button>
              )}
            </Space>
          </Card>

          <DashboardSummary
            salesTiers={dashboardData.salesTiers}
            viewMode={viewMode}
            isMobile={isMobile}
          />

          {viewMode === 'weekly' && <FlagGuide />}

          {!isMobile && (
            <GlobalSortBar
              sortColumn={globalSort.columnKey}
              sortOrder={globalSort.order}
              onClear={handleClearGlobalSort}
              tierCount={renderTiers?.length || 0}
            />
          )}

          {renderTiers?.map((tier) => {
            // Note: getTierBaselineData will handle filtering internally using the same logic
            // We don't need to pre-filter here as it will filter based on baselineWeek.endDate
            const baselineData = getTierBaselineData(tier.tierName, tier.locations);
            const baseline = baselineData?.baseline || null;
            const baselineDateRange = baselineData?.baselineDateRange || '';
            const baselineChanges = baselineData?.baselineChanges || null;

            return (
              <TierCard
                key={tier.tierName}
                tier={tier}
                sortBy={sortBy}
                isMobile={isMobile}
                viewMode={viewMode}
                dataStatus={dataStatus}
                columns={columns}
                baseline={baseline}
                baselineDateRange={baselineDateRange}
                baselineChanges={baselineChanges}
                baselineLoading={baselineLoading}
                baselineChangeLoading={baselineChangeLoading}
                allTiers={sortBy === 'dm' ? salesTierData?.salesTiers : undefined}
                anomalyFlags={sortBy === 'dm' ? anomalyFlags : undefined}
                baselineDataByTier={sortBy === 'dm' ? baselineDataByTier : undefined}
                dateRangeLabel={sortBy === 'dm' ? dashboardData.dateRangeLabel : undefined}
                startDate={selectedDateRange?.startDate}
                endDate={selectedDateRange?.endDate}
                globalSort={globalSort}
                onGlobalSortChange={setGlobalSort}
                filterNewLocations={tier.tierName === 'New Stores' ? undefined : filterByEightWeekRanges}
                dashboardEndDate={dashboardData?.endDate}
              />
            );
          })}
        </div>
      ) : null}
    </div>
  );
};

export default Dashboard;
